const Bmp = {
  /**
   * Validates on start or end tag
   *
   * @param {String} trimmedXml
   */
  validateXml(trimmedXml) {
    return !(trimmedXml.match(/^<MP/) === null || trimmedXml.match(/MP>$/) === null);
  },

  /**
   * Parse XML wit DOMParser
   * @private
   *
   * @param {String} trimmedXml
   * @param {String} tagName
   * @param {String} attributeName
   *
   * @return {String}
   * @throws {Error}
   * */
  getFromXml(trimmedXml, tagName, attributeName) {
    const parser = new DOMParser();
    // replace "&" with "&amp;" when parsing from xml string (otherwise the
    // Firefox implementation of DOMParser won't parse a company name like
    // "Apotheke & Co.")... we do not really use these values, the getFromXml()
    // method is mainly used to pre-validate a given xml, nevertheless, we
    // revert the replacement when returning the found value.
    const xmlDocument = parser.parseFromString(trimmedXml.replaceAll("&", "&amp;"), "text/xml");
    const value = xmlDocument.querySelector(tagName)?.getAttribute(attributeName);

    if (!value) {
      // eslint-disable-next-line unicorn/error-message -- an empty message signals "attribute missing" to getPatientBirthdate, which substitutes its own text
      throw new Error();
    }

    return value.replaceAll("&amp;", "&");
  },

  /**
   * Returns the current page number from the xml
   *
   * @param {String} trimmedXml
   * @return {Number}
   */
  getCurrentPageNumber(trimmedXml) {
    try {
      return Number.parseInt(this.getFromXml(trimmedXml, "MP", "a"));
    } catch {
      return 1;
    }
  },

  /**
   * Returns the max page number from the xml data
   *
   * @param {String} trimmedXml
   * @return {Number}
   */
  getTotalPageNumber(trimmedXml) {
    try {
      return Number.parseInt(this.getFromXml(trimmedXml, "MP", "z"));
    } catch {
      return 1;
    }
  },

  /**
   * Returns the max page number from the xml data
   *
   * @param {String} trimmedXml
   * @return {String}
   */
  getUniqueInstanceId(trimmedXml) {
    try {
      return this.getFromXml(trimmedXml, "MP", "U");
    } catch {
      throw new Error("fehlende eindeutige Instanz-ID");
    }
  },

  /**
   * Returns the patient firstname
   *
   * @param {String} trimmedXml
   * @return {String}
   */
  getPatientFirstname(trimmedXml) {
    try {
      return this.getFromXml(trimmedXml, "P", "g");
    } catch {
      throw new Error("ungültiger oder fehlender Vorname");
    }
  },

  /**
   * Returns the patient lastname
   *
   * @param {String} trimmedXml
   * @return {String}
   */
  getPatientLastname(trimmedXml) {
    try {
      return this.getFromXml(trimmedXml, "P", "f");
    } catch {
      throw new Error("ungültiger oder fehlender Nachname");
    }
  },

  /**
   * Returns the patient gender
   *
   * @param {String} trimmedXml
   * @return {?String}
   */
  getPatientGender(trimmedXml) {
    try {
      switch (this.getFromXml(trimmedXml, "P", "s")) {
        case "M":
          return "male";
        case "W":
          return "female";
        case "D":
          return "diverse";
        default:
          return null;
      }
    } catch {
      return null;
    }
  },

  /**
   * Returns the patient birthdate (YYYY-MM-DD)
   * from all possible input formats (YYYYMMDD,
   * YYYY-MM-DD, YYYY-M-D, DD.MM.YYYY, D.M.YYYY)
   *
   * @param {String} trimmedXml
   * @return {String}
   */
  getPatientBirthdate(trimmedXml) {
    try {
      const patientBirthdateAttribute = this.getFromXml(trimmedXml, "P", "b");

      // format: YYYYMMDD
      if (/^\d{8}$/.test(patientBirthdateAttribute)) {
        let year = patientBirthdateAttribute.slice(0, 4);
        let month = patientBirthdateAttribute.slice(4, 6);
        let day = patientBirthdateAttribute.slice(6, 8);
        return `${year}-${month}-${day}`;
      }

      // format: YYYY-MM-DD or YYYY-M-D
      let match = patientBirthdateAttribute.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (match) {
        let year = match[1];
        let month = match[2].padStart(2, "0"); // ensure two digits
        let day = match[3].padStart(2, "0"); // ensure two digits
        return `${year}-${month}-${day}`;
      }

      // format: DD.MM.YYYY or D.M.YYYY
      match = patientBirthdateAttribute.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (match) {
        let day = match[1].padStart(2, "0"); // ensure two digits
        let month = match[2].padStart(2, "0"); // ensure two digits
        let year = match[3];
        return `${year}-${month}-${day}`;
      }

      // None of the formats match
      throw new Error("ungültiges Format des Geburtsdatums");
    } catch (error) {
      throw new Error(error.message.length > 0 ? error.message : "fehlendes Geburtsdatum", {cause: error});
    }
  },

  /**
   * Returns the patient insurance number
   *
   * @param {String} trimmedXml
   * @return {?String}
   */
  getPatientInsurancenumber(trimmedXml) {
    try {
      return this.getFromXml(trimmedXml, "P", "egk");
    } catch {
      return null;
    }
  },
};

export default Bmp;
